"use client";

import { useMemo, useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

type BreakMode = "sentence" | "chars" | "words" | "delimiter";

const SENTENCE_SPLIT_RE = /(?<=[.!?])\s+/;
const WHITESPACE_RE = /\s+/;

function breakBySentence(text: string): string {
  return text
    .split(SENTENCE_SPLIT_RE)
    .map((sentence) => sentence.trim())
    .filter((sentence) => sentence.length > 0)
    .join("\n");
}

function breakByChars(text: string, limit: number): string {
  const words = text.split(WHITESPACE_RE).filter((word) => word.length > 0);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current.length === 0) {
      current = word;
    } else if (current.length + 1 + word.length <= limit) {
      current = `${current} ${word}`;
    } else {
      lines.push(current);
      current = word;
    }
  }
  if (current.length > 0) {
    lines.push(current);
  }
  return lines.join("\n");
}

function breakByWords(text: string, perLine: number): string {
  const words = text.split(WHITESPACE_RE).filter((word) => word.length > 0);
  const lines: string[] = [];
  for (let i = 0; i < words.length; i += perLine) {
    lines.push(words.slice(i, i + perLine).join(" "));
  }
  return lines.join("\n");
}

function breakByDelimiter(text: string, delimiter: string): string {
  if (delimiter.length === 0) {
    return text;
  }
  return text
    .split(delimiter)
    .map((part, index, parts) => {
      if (index === parts.length - 1) {
        return part;
      }
      return `${part}${delimiter}`;
    })
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join("\n");
}

function applyBreaks(
  text: string,
  mode: BreakMode,
  count: number,
  delimiter: string
): string {
  if (mode === "sentence") {
    return breakBySentence(text);
  }
  if (mode === "chars") {
    return breakByChars(text, Math.max(1, count));
  }
  if (mode === "words") {
    return breakByWords(text, Math.max(1, count));
  }
  return breakByDelimiter(text, delimiter);
}

/** English strings. A translated content file overrides these through `ui`. */
export const ADD_LINE_BREAKS_STRINGS = {
  breakMode: "Break mode",
  charactersPerLine: "Characters per line",
  wordsPerLine: "Words per line",
  delimiter: "Delimiter",
  yourText: "Your text",
  pasteYourWallOfText: "Paste your wall of text here…",
  addLineBreaks: "Add Line Breaks",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  output: "Output",
  line: "one: line|other: lines",
  sentenceModeLabel: "After every sentence",
  charsModeLabel: "Every N characters",
  wordsModeLabel: "Every N words",
  delimiterModeLabel: "After a delimiter",
} as const;

type Strings = typeof ADD_LINE_BREAKS_STRINGS;

function getBreakModes(
  strings: Strings
): { value: BreakMode; label: string }[] {
  return [
    { value: "sentence", label: strings.sentenceModeLabel },
    { value: "chars", label: strings.charsModeLabel },
    { value: "words", label: strings.wordsModeLabel },
    { value: "delimiter", label: strings.delimiterModeLabel },
  ];
}

export default function AddLineBreaksWidget() {
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<BreakMode>("sentence");
  const [count, setCount] = useState(80);
  const [delimiter, setDelimiter] = useState(",");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const outputLineCount =
    output.trim().length === 0 ? 0 : output.split("\n").length;
  const needsCount = mode === "chars" || mode === "words";

  function handleApply() {
    if (input.trim().length === 0) {
      return;
    }
    setOutput(applyBreaks(input, mode, count, delimiter));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(ADD_LINE_BREAKS_STRINGS);
  const breakModes = useMemo(() => getBreakModes(strings), [strings]);

  return (
    <>
      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.breakMode}
        </p>
        <div className="flex flex-wrap gap-3">
          {breakModes.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={mode === m.value}
                className="accent-primary"
                name="break-mode"
                onChange={() => setMode(m.value)}
                type="radio"
                value={m.value}
              />
              {m.label}
            </label>
          ))}
        </div>
        {needsCount && (
          <div className="flex items-center gap-3">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="break-count"
            >
              {mode === "chars"
                ? strings.charactersPerLine
                : strings.wordsPerLine}
            </label>
            <input
              className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="break-count"
              min={1}
              onChange={(e) => setCount(Number(e.target.value))}
              type="number"
              value={count}
            />
          </div>
        )}
        {mode === "delimiter" && (
          <div className="flex items-center gap-3">
            <label
              className="font-medium text-foreground text-sm"
              htmlFor="break-delimiter"
            >
              {strings.delimiter}
            </label>
            <input
              className="w-24 rounded-lg border border-border bg-card px-3 py-2 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="break-delimiter"
              maxLength={5}
              onChange={(e) => setDelimiter(e.target.value)}
              placeholder=","
              type="text"
              value={delimiter}
            />
          </div>
        )}
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="break-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="break-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.pasteYourWallOfText}
          value={input}
        />
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleApply}
          type="button"
        >
          {strings.addLineBreaks}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="break-output"
          >
            {strings.output}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="break-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {outputLineCount}
            {tPlural(strings.line, locale, outputLineCount)}
          </p>
        </div>
      )}
    </>
  );
}
